import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { GridCellParams, GridColDef, GridSortModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getAllInvoices } from 'src/shared/services/invoiceService';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Clear, SimCardDownload, Email } from '@mui/icons-material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { downloadInvoicePdf } from 'src/shared/functions/downloadInvoicePdf';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { formatDate } from 'src/shared/functions/formatDate';
import { Colors } from 'src/shared/components/styles/colors';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { InvoiceSendingTypeStringEnum } from 'src/shared/models/enums/InvoiceSendingTypeString.enum';
import { Invoice } from '../models/invoice';
import { InvoiceStatuses } from './invoiceStatuses';
import { InvoiceSearchFilter } from '../models/invoiceSearchFilter';
import HandleInvoicesButton from './handleInvoicesButton';

type InvoiceDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: InvoiceSearchFilter;
  searchFilterCallback: (filter: InvoiceSearchFilter) => void;
  changeOcrMode?: boolean;
  changeOcrCallback?: (invoice: Invoice) => void;
  hideCheckBoxSelection?: boolean;
  setTotalInvoices?: (totalItems: number) => void;
  refetchData?: boolean;
  setRefetchData?: (refetch: boolean) => void;
};

const InvoiceDataGrid = (props: InvoiceDataGridProps) => {
  const {
    loadingCallback,
    searchFilter,
    searchFilterCallback,
    changeOcrMode = false,
    changeOcrCallback = undefined,
    hideCheckBoxSelection = false,
    setTotalInvoices = undefined,
    refetchData = undefined,
    setRefetchData = undefined,
  } = props;
  const { t } = useTranslation(['member', 'invoice', 'common']);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [loadingPDF, setLoadingPDF] = useState<boolean>(false);
  const [rows, setRows] = useState<Invoice[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'ocr', sort: 'desc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (data.pageSize !== searchFilter.pageSize || data.page !== searchFilter.pageIndex) {
      setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
    }
  }, [data.page, data.pageSize, searchFilter.pageIndex, searchFilter.pageSize]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    setTimeout(() => {
      scrollIntoView();
    }, 100);
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }
    try {
      const result = await getAllInvoices(searchFilter.pageIndex, searchFilter.pageSize, sortModel[0].field, sortModel[0].sort, searchFilter);

      const invoiceResult = extendObjectWithId(result.data.results, 'invoice_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        if (setTotalInvoices) {
          setTotalInvoices(result.data.totalItems);
        }
        ConsoleHelper.log(invoiceResult, 'invoiceResult');

        setTimeout(() => {
          setRows(invoiceResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        loadingCallback(false);
        updateData('loading', false);

        if (refetchData && setRefetchData) {
          setRefetchData(false);
        }
      }
    }
  }, [isMounted, loadingCallback, searchFilter, sortModel, setTotalInvoices, refetchData, setRefetchData]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [data.page, data.pageSize, sortModel, fetchData, isMounted]);

  useEffect(() => {
    if (refetchData) {
      fetchData();
    }
  }, [fetchData, refetchData]);

  const pdfLoadingCallback = useCallback((isLoading: boolean) => {
    setLoadingPDF(isLoading);
  }, []);

  const downloadPdf = async (id) => {
    if (
      searchFilter.sendingType === InvoiceSendingTypeIntegerEnum.Reminder1 ||
      searchFilter.sendingType === InvoiceSendingTypeIntegerEnum.Reminder2
    ) {
      downloadInvoicePdf(
        id,
        pdfLoadingCallback,
        searchFilter.sendingType === InvoiceSendingTypeIntegerEnum.Reminder1
          ? InvoiceSendingTypeStringEnum.reminder1
          : InvoiceSendingTypeStringEnum.reminder2,
      );
    } else {
      downloadInvoicePdf(id, pdfLoadingCallback);
    }
  };

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.InvoicesDetail}/${params.row.id}`);
  };

  // OCR, Medlem, Belopp, Betalt belopp och Förfallodatum

  const columns: GridColDef[] = [];

  if (changeOcrMode) {
    columns.push(
      {
        field: 'ocr',
        headerName: t('invoice:OCR'),
        flex: 1,
        renderCell: (params: GridCellParams) => {
          return (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <span>{params.row.ocr}</span>
              <Button
                size='small'
                variant='contained'
                color='primary'
                sx={{ py: 0.5, px: 1, lineHeight: 1.25, ml: 2 }}
                onClick={() => {
                  if (changeOcrCallback !== undefined) changeOcrCallback(params.row);
                }}
                disableElevation
              >
                {t('common:button.choose')}
              </Button>
            </Box>
          );
        },
      },
      {
        field: 'memberNr',
        headerName: t('invoice:MemberNr'),
        flex: 0.5,
        sortable: false,
        renderCell: (params: GridCellParams) => {
          return params.row.member !== null ? <>{params.row.member.memberNr ?? ''}</> : null;
        },
      },
      {
        field: 'member',
        headerName: t('invoice:Member'),
        flex: 1,
        sortable: false,
        renderCell: (params: GridCellParams) => {
          return params.row.member !== null ? (
            <span>
              {params.row.member.firstName ?? ''} {params.row.member.lastName ?? ''}
            </span>
          ) : null;
        },
      },
      {
        field: 'amount',
        headerName: t('invoice:amount'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.9,
        minWidth: 105,
        renderCell: (params: GridCellParams) => {
          const { amount } = params.row;
          return (amount && <AmountWithCurrency amount={amount} />) || '-';
        },
      },
      {
        field: 'dueDate',
        headerName: t('invoice:dueDate'),
        flex: 0.6,
        renderCell: (params: GridCellParams) => {
          const { dueDate } = params.row;
          return formatDate(dueDate);
        },
      },
      {
        field: 'paid',
        headerName: t('invoice:paid'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.6,
        minWidth: 105,
        renderCell: (params: GridCellParams) => {
          const { paid } = params.row;
          return (paid && <AmountWithCurrency amount={paid} />) || '-';
        },
      },
    );
  } else {
    columns.push(
      {
        field: 'ocr',
        headerName: t('invoice:OCR'),
        flex: 1,
        renderCell: (params: GridCellParams) => {
          return <>{params.row.ocr}</>;
        },
      },
      {
        field: 'member',
        headerName: t('invoice:Member'),
        flex: 1,
        sortable: false,
        renderCell: (params: GridCellParams) => {
          return params.row.member !== null ? (
            <span>
              {params.row.member.firstName ?? ''} {params.row.member.lastName ?? ''}
            </span>
          ) : null;
        },
      },
      {
        field: 'category',
        headerName: t('invoice:InvoiceType'),
        sortable: false,
        flex: 0.75,
        renderCell: (params: GridCellParams) => {
          return params.row.category !== null ? <span>{params.row.category.text}</span> : null;
        },
      },
      {
        field: 'amount',
        headerName: t('invoice:amount'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.9,
        minWidth: 105,
        renderCell: (params: GridCellParams) => {
          const { amount } = params.row;
          return (amount && <AmountWithCurrency amount={amount} />) || '-';
        },
      },
      {
        field: 'dueDate',
        headerName: t('invoice:dueDate'),
        flex: 1,
        renderCell: (params: GridCellParams) => {
          const { dueDate } = params.row;
          return formatDate(dueDate);
        },
      },
      {
        field: 'paid',
        headerName: t('invoice:paid'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.75,
        minWidth: 105,
        renderCell: (params: GridCellParams) => {
          const { paid } = params.row;
          return (paid && <AmountWithCurrency amount={paid} />) || '-';
        },
      },
      {
        field: 'paidDate',
        headerName: t('invoice:paidDate'),
        flex: 1,
        renderCell: (params: GridCellParams) => {
          const { paidDate } = params.row;
          return paidDate ? formatDate(paidDate) : '-';
        },
      },
      {
        field: 'invoiceMission_id',
        headerName: t('invoice:invoiceMission'),
        sortable: false,
        flex: 1,
        renderCell: (params: GridCellParams) => {
          const { mission } = params.row;
          return mission?.missionName ? (
            <Tooltip title={mission.missionName}>
              <span>{mission.missionName}</span>
            </Tooltip>
          ) : null;
        },
      },
      {
        field: 'status',
        headerName: t('invoice:invoiceStatusLabel'),
        sortable: false,
        width: 100,
        renderCell: (params: GridCellParams) => {
          const { status: stat } = params.row;
          return <InvoiceStatuses status={stat} chipSize='small' />;
        },
      },
      {
        field: 'isSent',
        headerName: '', // t('invoice:sent'),
        sortable: false,
        width: 40,
        renderCell: (params: GridCellParams<{ isSent: boolean | null | undefined }>) => {
          const isSent = params.row.isSent as boolean;
          return (
            <Tooltip title={isSent ? t('invoice:sent') : t('invoice:unSent')} arrow>
              <span>{isSent ? <Email fontSize='small' sx={{ color: 'grey' }} /> : null}</span>
            </Tooltip>
          );
        },
      },
      {
        field: 'invoice_id',
        headerName: '',
        width: 40,
        renderCell: (params: GridCellParams) => {
          return (
            <Tooltip title={t('invoice:DownloadAsPDF')}>
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPdf(params.row.invoice_id);
                  }}
                  style={{ display: 'flex', color: Colors.LinkColor }}
                  size='small'
                >
                  <SimCardDownload />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <LoadingOverlay message={t('invoice:loadingPdf')} loading={loadingPDF} />
      <DataGrid<Invoice>
        autoHeight
        autoRowHeight={false}
        columns={columns}
        rows={rows}
        dataModel={data}
        onRowClick={!changeOcrMode ? onRowClick : undefined}
        disableSelectionOnClick
        checkboxSelection={!hideCheckBoxSelection}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
      />
      {selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <HandleInvoicesButton rows={rows} selectionModel={selectionModel} fetchData={fetchData} setSelectionModel={setSelectionModel} />
          </Box>
        </FixedFooter>
      )}
    </Box>
  );
};

export default InvoiceDataGrid;
